import type { BadgeVariant } from '@/components/ui'
import { FALLBACK_CURRENCY } from '@/lib/formatters'
import type {
  LedgerActorRef,
  LedgerTransactionType,
  LedgerTransactionStatus,
} from '../types/security-ledger.types'

/**
 * Fallback only. The real currency cascades from the org/company settings
 * (Organization.settings.defaultCurrency, overridable per company) and is
 * returned by the ledger endpoints — pass it into the formatters below.
 */
export const DEFAULT_CURRENCY = FALLBACK_CURRENCY

const amountFmt = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

/** Plain grouped number, e.g. "1,234.5". */
export function formatLedgerNumber(amount: number): string {
  return amountFmt.format(amount)
}

/** Number prefixed with the given currency, e.g. "USD 1,234.5". */
export function formatLedgerAmount(
  amount: number,
  currency: string = DEFAULT_CURRENCY,
): string {
  return `${currency || DEFAULT_CURRENCY} ${amountFmt.format(amount)}`
}

export const TYPE_LABEL: Record<LedgerTransactionType, string> = {
  deduction: 'Deduction',
  refund: 'Refund',
  manual_adjustment: 'Adjustment',
}

export const STATUS_LABEL: Record<LedgerTransactionStatus, string> = {
  pending_approval: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
}

export const STATUS_VARIANT: Record<LedgerTransactionStatus, BadgeVariant> = {
  pending_approval: 'warning',
  approved: 'success',
  rejected: 'danger',
}

/**
 * Sign convention is balance-centric (consistent across both ledger tables):
 * deductions and adjustments RAISE the held deposit (+), refunds LOWER it (−).
 */
export function raisesBalance(type: LedgerTransactionType): boolean {
  return type === 'deduction' || type === 'manual_adjustment'
}

/** Signed amount reflecting the effect on the held balance, e.g. "+1,234" / "−500". */
export function signedLedgerNumber(
  type: LedgerTransactionType,
  amount: number,
): string {
  return `${raisesBalance(type) ? '+' : '−'}${formatLedgerNumber(amount)}`
}

/** Display name for a populated actor ref; '—' when only an id (or nothing) is present. */
export function actorName(ref: LedgerActorRef | undefined | null): string {
  if (!ref || typeof ref === 'string') return '—'
  const name = [ref.firstName, ref.lastName].filter(Boolean).join(' ').trim()
  return name || ref.email || '—'
}
